import {
  InteractionRequiredAuthError,
  RedirectRequest,
  SilentRequest,
} from '@azure/msal-browser';

/**
 * Error codes that require user interaction
 * Based on frontend/insite implementation
 */
const INTERACTION_REQUIRED_ERROR_CODES = new Set([
  // Official MSAL documented error codes
  'interaction_required',
  'login_required',
  'consent_required',
  // Additional documented MSAL error codes that require interaction
  'token_renewal_error',
  'no_account_in_silent_request',
  // Other common interaction-required scenarios
  'no_account_error',
  'no_tokens_found',
  'user_login_error',
  // AADSTS error codes that commonly require interaction
  'aadsts50058', // User needs to sign in
  'aadsts50076', // Multi-factor authentication required
  'aadsts50079', // User needs to enroll in multi-factor authentication
  'aadsts50173', // Fresh sign-in hint required
  'aadsts700054', // Response_type 'id_token' requires response_mode 'form_post'
]);

/**
 * Get error code from various error formats
 */
const getLowerCaseErrorCode = (error: unknown): string | null => {
  if (!error) {
    return null;
  }

  if (typeof error === 'string') {
    return error.toLowerCase();
  }

  const errorLike = error as {
    errorCode?: string;
    code?: string;
    name?: string;
    message?: string;
  };
  const code = errorLike?.errorCode ?? errorLike?.code ?? null;

  if (typeof code === 'string' && code.length > 0) {
    return code.toLowerCase();
  }

  if (typeof errorLike?.message === 'string' && errorLike.message.length > 0) {
    return errorLike.message.toLowerCase();
  }

  return null;
};

/**
 * Get the current page URL for redirect
 */
const getRedirectStartPage = (): string | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  const { pathname, search, hash } = window.location;
  return `${pathname ?? ''}${search ?? ''}${hash ?? ''}`;
};

/**
 * Build an interactive login request with consistent configuration
 * Adopted from frontend/insite implementation
 */
export const buildInteractiveLoginRequest = (
  request?: RedirectRequest,
): RedirectRequest => {
  const sanitized: RedirectRequest = { scopes: [], ...request };

  // Remove 'login' prompt as it can cause issues
  if (
    typeof sanitized.prompt === 'string' &&
    sanitized.prompt.toLowerCase() === 'login'
  ) {
    delete sanitized.prompt;
  }

  // Default to select_account if no prompt specified
  if (!sanitized.prompt) {
    sanitized.prompt = 'select_account';
  }

  // Set redirect start page to current location
  const redirectStartPage = getRedirectStartPage();
  if (redirectStartPage && !sanitized.redirectStartPage) {
    sanitized.redirectStartPage = redirectStartPage;
  }

  // Ensure scopes are set from environment
  if (!sanitized.scopes || sanitized.scopes.length === 0) {
    const fullScope = import.meta.env.VITE_AZURE_SCOPE;
    if (fullScope) {
      sanitized.scopes = fullScope.split(' ');
    }
  }

  // PKCE (code challenge) is generated automatically by msal-browser for the
  // auth code flow — it is not a request option.

  return sanitized;
};

/**
 * Whether the sign-in library says only the user can fix this: its interaction-required
 * error or one of the listed codes. An unknown error is not one, so a network failure
 * never reads as signed out (AU-2).
 */
export const isInteractionRequiredError = (error: unknown): boolean => {
  if (error instanceof InteractionRequiredAuthError) {
    return true;
  }
  const errorCode = getLowerCaseErrorCode(error);
  return errorCode !== null && INTERACTION_REQUIRED_ERROR_CODES.has(errorCode);
};

/**
 * Create a standardized login request for the mobile app
 */
export const createMobileLoginRequest = (): RedirectRequest => {
  const fullScope = import.meta.env.VITE_AZURE_SCOPE;

  return buildInteractiveLoginRequest({
    scopes: fullScope ? fullScope.split(' ') : [],
    prompt: 'select_account',
  });
};

/**
 * Create a standardized silent request for token refresh
 */
export const createSilentTokenRequest = (account?: any): SilentRequest => {
  const fullScope = import.meta.env.VITE_AZURE_SCOPE;

  return {
    scopes: fullScope ? fullScope.split(' ') : [],
    account: account,
    forceRefresh: false,
  };
};
